import {SourceLoader , Source} from "./SourceLoader.js";
import IIIFResources from "./IIIFResources.js";
import { Subject, fromEvent } from "rxjs";
import { sample, debounceTime, first } from "rxjs/operators";


const _debug = false;

const defaultConfig = {
    element: "", //DOM-element or query-selector for one
    fittingMode: "fixed",   //How the canvas is fit to the image, one of "fixed" (no fitting), "toWidth" (adapt height to fit image), "toHeight" (adapt width to fit image)
    useTiles: true, //whether the image should be constructed as a tiled image if possible. Should generally be true
    margins: {  //margins for the canvas. values can be numbers or functions returning numbers
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    },
    zoom: {
        enabled: true,
        max: 5,
        min: 1,
        speed: 1.25, 
        persistence: {
            enabled: false, 
        	rotation: true,
        	persistenceId: "",
        }
    },
    controls: {
        zoom: {
            slider: "#zoomSlider",
            zoomIn: "#zoomInButton",
            zoomOut: "#zoomOutButton",
            home: "zoomHomeButton"
        },
        rotation: {
            rotateLeft: "#rotateLeft",
            rotateRight: "#rotateRight",
        },
        reset: "#resetView"
    }, 
    sequence : {
        columns: 1,     //number of images to display side-by-side
        columnOffset: 0, //at which column should the first image appear
        rowMargin: 0.1    //offset in openseadragon coordintes between rows
    },
    navigator: {
        enabled: false
    }
}

const imageOptions = {
    source: [], //url string, iiif image info object or array thereof
    footer: {
        baseURl: "",
    },
    highlightCoords: []
}


export default class Image {

    constructor(config) {
        if(_debug) console.log("IMAGE VIEW v0.2.2");
        this.config = jQuery.extend(true, {}, defaultConfig, config);
        if(_debug) console.log("load Viewer with config ", this.config);
        this.element = typeof this.config.element === "string" ? document.querySelector(this.config.element) : this.config.element;
        const osConfig = {
            element: this.element,
            showZoomControl : false,
            showHomeControl: false,
            showFullPageControl: false,
            showRotationControl: false,
            homeFillsViewer: true,
            collectionMode: false,
            minZoomImageRatio: 0,
            zoomPerScroll: this.config.zoom.enabled ? 1.2 : 1.0,
            showNavigator: this.config.navigator.enabled,
            gestureSettingsMouse: {
                clickToZoom: false
            }
        }
        this.viewportMargins = {
            top: _getOrResolve(this.config.margins.top),
            bottom: _getOrResolve(this.config.margins.bottom),
            left: _getOrResolve(this.config.margins.left),
            right: _getOrResolve(this.config.margins.right),
        }
        if(_debug)console.log("opeanseadragon config ", osConfig);
        this.openseadragon = OpenSeadragon(osConfig);
        this.extent = new Extent(this.element, this.viewportMargins, this.config.fittingMode, this.config.sequence);

        this.onAnimationFinish = new Subject();
        this.onRedraw = new Subject();
        this.onUpdate = new Subject();
        this.onOpened = new Subject();
        this.onRotate = new Subject();
        this.onClick = new Subject();
        this.onRotated = this.onRotate.pipe(
            sample(this.onAnimationFinish),
        );
        this.onZoom = new Subject();
        this.onPan = new Subject();
        this.onZoomed = this.onZoom.pipe(
            sample(this.onAnimationFinish),
        );

        this.onResize = new Subject();
        this.resizeObserver = new ResizeObserver(e =>  {
            this.onResize.next(e);
        });
        this.resizeObserver.observe(this.element);

    }

    load(options, imageToShow) {
        if(_debug)console.log("loading ", options);
        this.currentImageIndex = imageToShow ? imageToShow : 0;
        let promises = []; 
        if(!Array.isArray(options) && typeof options == 'object' && options.source) {
            promises = _loadSources(options.source);
            this.page = options.page;
        } else {
            promises = _loadSources(options);
             this.page = 1;
        }

        return new Promise((resolve, reject) => {
            Promise.all(promises).then(results => {
                this.sources = results;
                this.tileSources = this.sources.map(s => _createTileSource(s, this.config));
                
                if(_debug)console.log("open openseadragon with tile sources", this.tileSources);

                this.extent.setSize(this.tileSources);
                this.openseadragon.open(this.tileSources, this.page);
                this.openseadragon.addOnceHandler("open", event => _arrangeImageSequence(this.openseadragon, this.config.sequence));        
                this.openseadragon.addHandler("rotate", event => this.onRotate.next(event));
                this.openseadragon.addHandler("animation-finish", event => this.onAnimationFinish.next(event));
                this.openseadragon.addHandler("zoom", event => this.onZoom.next(event));
                this.openseadragon.addHandler("pan", event => this.onPan.next(event));
                this.openseadragon.addHandler( 'update-viewport', event => this.onUpdate.next(event));
                this.openseadragon.addHandler("canvas-click", e => this.onClick.next(e));
                this.openseadragon.addOnceHandler("tile-loaded", event => {
                    this.onOpened.next(event);
                    resolve(event);
                });
                this.openseadragon.addOnceHandler("open-failed", event => {
                    this.onOpened.error(event);
                    reject(event);
                });
                this.openseadragon.addOnceHandler("tile-load-failed", event => {
                    this.onOpened.error(event);
                    reject(event);
                });
            })
            .catch(e => reject(e))
        });
    }

    getCanvasContext() {
        if(this.openseadragon) {             
            return this.openseadragon.drawer.context;
        } else {
            return undefined;
        }
    }

    isOriginClean() {
        let context = this.getCanvasContext();
        if(context) {
            try {                 
                context.getImageData(0,0,1,1);
                return true;
            } catch(e) {
                return false;
            }
        } else {
            return undefined;
        }
    }

    getCurrentImage() {
        return this.openseadragon.world.getItemAt(this.currentImageIndex);
    }

    setCurrentImage(index) {
        this.currentImageIndex = index;
    } 

    getCurrentRow() {
        let currentImagePosition = this.currentImageIndex + this.config.sequence.columnOffset;
        let rowStartIndex = Math.floor(currentImagePosition/this.config.sequence.columns) * this.config.sequence.columns;
        return this.openseadragon?.world._items.slice(rowStartIndex, rowStartIndex + this.config.sequence.columns);
    }

    getImageIndexById(imageId) {
        for (let index = 0; index < this.openseadragon.world.getItemCount(); index++) {
            const image = this.openseadragon.world.getItemAt(index);
            if(image && IIIFResources.getId(image.source) === imageId) {
                return index;
            }
        }
    }

    getImageById(imageId) {
        for (let index = 0; index < this.openseadragon.world.getItemCount(); index++) {
            const image = this.openseadragon.world.getItemAt(index);
            if(image && IIIFResources.getId(image.source) === imageId) {
                return image;
            }
        }
    }

    getOriginalImageSize() {
        return new OpenSeadragon.Point(this.extent.getWidth(this.tileSources), this.extent.getHeight(this.tileSources));
    }

    getRotation() {
        return this.openseadragon?.viewport?.getRotation() ?? 0;
    }

    isRotated() {
        let rotation = this.openseadragon?.viewport?.getRotation() ?? 0;
        return this.extent.isRotated(rotation);
    }
 
    enablePan() {
        if(this.openseadragon) {
            this.openseadragon.panVertical = true;
            this.openseadragon.panHorizontal = true;
        }
    }

    disablePan() {
        if(this.openseadragon) {
            this.openseadragon.panVertical = false;
            this.openseadragon.panHorizontal = false;
        }
    }

    redraw() {
        let rotation = this.openseadragon.viewport?.getRotation() ?? 0;
        if( (((rotation%360)+360) % 90) == 0) {
            this.viewportMargins = _rotateMargins({
                top: _getOrResolve(this.config.margins.top),
                bottom: _getOrResolve(this.config.margins.bottom),
                left: _getOrResolve(this.config.margins.left),
                right: _getOrResolve(this.config.margins.right),
            }, rotation);
        }
        
        this.extent.setSize(this.tileSources, rotation);
        this.onResize.pipe(debounceTime(100), first()).subscribe(e => this.onRedraw.next());
        this.onResize.next();
    }

    close() {
        this.openseadragon?.close();
        this.onAnimationFinish.complete();
        this.onRedraw.complete();
        this.onUpdate.complete();
        this.onOpened.complete();
        this.onRotate.complete();
        this.onClick.complete();
        this.onRotated.complete();
        this.onZoom.complete();
        this.onPan.complete();
        this.onZoomed.complete();
        this.onResize.complete();
        this.resizeObserver.disconnect();
    }

}
 
class Extent {

    constructor(element, viewportMargins, fittingMode, sequenceConfig) { 
        this.element = element;
        this.footerHeight = viewportMargins.bottom;
        this.fittingMode = fittingMode;
        this.sequenceConfig = sequenceConfig;
        this.calculateSizes();
    }


    calculateSizes(tileSources, rotation) {
        rotation ??= 0;
        let xMargin = this.footerHeight * (this.isRotated(rotation) ? 1 : 0);
        let yMargin = this.footerHeight * (this.isRotated(rotation) ? 0 : 1);
        this.originalCanvasSize = {
             x: this.element.clientWidth,
             y: this.element.clientHeight
        }
        if(tileSources && tileSources.length && this.fittingMode == 'toWidth') {
             if(_debug) {
                 console.log("rotation: " + rotation);
                 console.log("height: ", this.getHeight(tileSources, rotation));
                 console.log("width: ", this.getWidth(tileSources, rotation));
                 console.log("canvas size ", this.originalCanvasSize);
             }
            this.canvasSize = new OpenSeadragon.Point(
                this.originalCanvasSize.x,
                this.originalCanvasSize.x * this.getHeight(tileSources, rotation) / this.getWidth(tileSources, rotation) + this.footerHeight)
        } else if(tileSources && tileSources.length && this.fittingMode == 'toHeight') {
            this.canvasSize = new OpenSeadragon.Point(
                (this.originalCanvasSize.y - this.footerHeight) * this.getWidth(tileSources, rotation) / this.getHeight(tileSources, rotation),
                this.originalCanvasSize.y)
        } else {
            this.canvasSize = new OpenSeadragon.Point(
                this.originalCanvasSize.x,
                this.originalCanvasSize.y
            )
        }
    }

    getOSCanvasSize(openseadragon) {
        return openseadragon.viewport.viewerElementToViewportCoordinates(this.canvasSize);
    }

    getOSFooterHeight(image, rotation) {
        if(this.isRotated(rotation)) {
            return image.imageToViewportCoordinates(new OpenSeadragon.Point(this.footerHeight, 0)).x;
        } else {
            let pointImage = image.viewerElementToImageCoordinates(new OpenSeadragon.Point(0,this.footerHeight));
            let pointViewport = image.imageToViewportCoordinates(pointImage);
            console.log("footer height ", this.footerHeight, pointImage.y, pointViewport.y);
            return pointViewport.y;
        }
    }

    setSize(tileSources, rotation) {
        this.calculateSizes(tileSources, rotation);
        let xMargin = this.footerHeight * this.isRotated(rotation) ? 1 : 0;
        let yMargin = this.footerHeight * this.isRotated(rotation) ? 0 : 1;
        if(_debug)console.log("Set canvas size ", this.canvasSize, this.fittingMode);
        if(this.fittingMode !== "fixed") {
            this.element.style.width = (this.canvasSize.x + xMargin) + "px";
            this.element.style.height = (this.canvasSize.y + yMargin) + "px";
        }
    }

    getHeight(tileSources, rotation) {
        if(this.isRotated(rotation)) {
            return this.getWidth(tileSources);
        } else {
            let columns = this.sequenceConfig.columns;
            let height = 0;
            for (let i = 0-this.sequenceConfig.columnOffset; i < tileSources.length; i += columns) {
                let rowImages = tileSources.slice(Math.max(0,i), i+columns);
                let rowHeight = rowImages.map(s => _getSizeScaledToOtherImage(s, rowImages[0]).y).reduce((x,y) => Math.max(x,y));
                height = Math.max(height, rowHeight)
            }
            return height;
        }
    }

    getTotalHeight(tileSources, rotation) {
        if(this.isRotated(rotation)) {
            return this.getWidth(tileSources);
        } else {
            let columns = this.sequenceConfig.columns;
            let height = 0;
            for (let i = 0-this.sequenceConfig.columnOffset; i < tileSources.length; i += columns) {
                let rowImages = tileSources.slice(Math.max(0,i), i+columns);
                let rowHeight = rowImages.map(s => _getSizeScaledToOtherImage(s, rowImages[0]).y).reduce((x,y) => Math.max(x,y)) + this.sequenceConfig.rowMargin;
                height += rowHeight;
            }
            height -= this.sequenceConfig.rowMargin;
            return height;
        }
    }

    getWidth(tileSources, rotation) {
        if(this.isRotated(rotation)) {
            return this.getHeight(tileSources);
        } else {
            let columns = this.sequenceConfig.columns;
            let rows = Math.ceil((tileSources.length + this.sequenceConfig.columnOffset) / columns);
            let width = 0;
            for (let i = 0-this.sequenceConfig.columnOffset; i < tileSources.length; i += columns) {
                let rowImages = tileSources.slice(Math.max(0,i), i+columns);

                const baseHeight = rowImages[0].dimensions.height;

                let rowWidth = 0;
                for (let rowIndex = 0; rowIndex < rowImages.length; rowIndex++) {
                    const image = rowImages[rowIndex];
                    if(rowIndex == 0) {
                        rowWidth += image.dimensions.x;
                    } else {
                        const scaledSize = _getSizeScaledToOtherImage(image, rowImages[0]);
                        rowWidth += scaledSize.x;
                    }
                }

                width = Math.max(width, rowWidth);
            }
            return width;
        }
    }

    isRotated(rotation) {
        return Math.abs(rotation % 180) == 90;
    }

    getRotated(dimension, rotation) {
        if(this.isRotated(rotation)) {
            return {
                x: dimension.y,
                y: dimension.x
            }
        } else {
            return {
                x: dimension.x,
                y: dimension.y
            } 
        }
    }
}


function _arrangeImageSequence(openseadragon, sequenceConfig) {
    var i, tiledImage, previousImage;
    var count = openseadragon.world.getItemCount();
    for (i = 0; i < count; i++) {
        let position = i + sequenceConfig.columnOffset;
        let column = position % sequenceConfig.columns;
        let row = 0;
        if(position >= sequenceConfig.columns) {
            previousImage = openseadragon.world.getItemAt(Math.max(0, i-sequenceConfig.columns));
            if(previousImage) {
                row = previousImage.getBounds().getBottomRight().y + sequenceConfig.rowMargin;
            }
        }
        tiledImage = openseadragon.world.getItemAt(i);
        tiledImage.setPosition(new OpenSeadragon.Point(column, row), true);

        if(column > 0 && i > 0) {//not te first column and has preceeding image 
            const imageScale =  openseadragon.world.getItemAt(i-1).getBounds().height / tiledImage.getBounds().height
            const scaledSize = new OpenSeadragon.Point(tiledImage.getBounds().width * imageScale, tiledImage.getBounds().height * imageScale);
            tiledImage.setWidth(scaledSize.x);
            tiledImage.setHeight(scaledSize.y); 
            tiledImage.scale = imageScale;
        }
    }
}

function _getSizeScaledToOtherImage(image, otherImage) {
    const scale = otherImage.dimensions.y / image.dimensions.y;
    return new OpenSeadragon.Point(image.dimensions.x * scale, image.dimensions.y * scale);
}



function _loadSources(sources) {
    if(!(sources instanceof Array)) {
        sources = [sources];
    }
    return sources.map(s => {
        if(s instanceof Source) {
            return s;
        } else {
            return new SourceLoader().getSource(s);
        }
    }); 
}

function _getOrResolve(x) {
    if(typeof x == 'function') {
        return x();
    } else {
        return x;
    }
}

/**
 * Creates a tilesource object usable by the OpenSeadragon viewer from a url or json-object
 * @param source  A Source object
 * @return a promise resolved when any urls are loaded - if no urls need to be loaded, the promise resolves immediately
 */
function _createTileSource(source, config) {
if(_debug) console.log("resolve tile source ", source);
let tileSource;
if(config.useTiles && source.type === "iiif") {
    source.json.crossOriginPolicy = "Anonymous";
    tileSource = new OpenSeadragon.IIIFTileSource(source.json);
} else {              
    source.json.crossOriginPolicy = "Anonymous";
    tileSource  = _createPyramid(source, config);                    
}
return tileSource;                
}

/**
 * creates a OpenSeadragon.LegacyTileSource (pyramid image source) from the given imageInfo object, which may either
 * be a IIIF imageInfo json-object or a list of image resource objects - consisting
 * each of a url, a width and a height
 * @param source  A Source object
 * @return the tilesource usable by OpenSeadragon
 */
function _createPyramid( source, config ) {
    if(_debug) {
        console.log("Creating legacy tilesource from source", source);
    }
    var imageLevels = [];
    var tileSource;
    if(source.json.sizes) {
        source.json.sizes.forEach(function(size, index) {
            if(!size.height) {
                var ratio = source.json.width/source.json.height;
                size.height = Math.round(size.width / ratio);
            } else if(!size.width) {
                var ratio = source.json.width/source.json.height;
                size.width = Math.round(size.width * ratio);
            }
            if(_debug) {                    
                console.log("Image level width = ", size.width)
                console.log("Image level height = ", size.height)
            }
            var sizeParam = size.width + "," + size.height;
            if( ( !size.width || size.width >= imageInfo.width) && (!size.height || size.height >= source.json.height) ) {
                sizeParam = "max";
            }
            
            var level = {
                mimetype: _getDisplayMimetype(source.mimetype),
                url: _getId(source.json).replace( "/info.json", "" ) + "/full/" + sizeParam + "/0/default." + _getDisplayFormatForSource(source),
                crossOriginPolicy: "Anonymous",
                width: source.json,
                height: source.json,
            };
            
            if(_debug) {
                console.log("Created level ", level);
            }
            
            imageLevels.push( level );
        });
        tileSource = new OpenSeadragon.LegacyTileSource(imageLevels);

    } else {
        tileSource = new OpenSeadragon.ImageTileSource({
            url: _getId(source.json).replace( "/info.json", "" ) + "/full/full/0/default." + _getDisplayFormatForSource(source),
            crossOriginPolicy: "Anonymous",
            buildPyramid: false
        });
    }
    tileSource.id = _getId(source.json);
    return tileSource;
}

function _getDisplayFormatForSource(source) {
    if(source.type === "iiif") {
        if(source.json?.preferredFormats && source.json?.preferredFormats.length) {
            return _getDisplayFormat(source.json.preferredFormats[0]);
        } else {
            return "jpg";
        }
    } else {
        return _getFileExtension(_getDisplayMimetype(source.mimetype));
    }
}

function _getDisplayMimetype(mimetype) {
    if(mimetype) {
        switch(mimetype.toLowerCase()) {
            case "image/tif":
            case "image/tiff":
            case "image/jp2":
            case "image/jpx":
            case "application/json":
            case "application/ld+json":
            case "application/json+ld":
                return "image/jpeg";
            default:
                return mimetype.toLowerCase();
        }
    } else {
        return "";
    }
}

function _getDisplayFormat(extension) {
    if(extension) {
        switch(extension.toLowerCase()) {
            case "tif":
            case "tiff":
            case "jp2":
            case "jpx":
                return "jpg";
            default:
                return extension.toLowerCase();
        }
    } else {
        return "";
    }
}

function _getFileExtension(mimetype) {
    if(mimetype) {
        switch(mimetype.toLowerCase()) {
            case "image/jpeg":
            case "image/jpg":
                return "jpg";
            case "image/tif":
            case "image/tiff":
                return "tif";
            case "image/jp2":
            case "image/jpx":
                return "jp2";
            default:
                return mimetype.replace(/.*\//, "");
        }
    } else {
        return "";
    }
}

function _getId(json) {
    if(typeof json === "string") {
        return json;
    } else if(typeof json === "object") {
        if(json.id) {
            return json.id;
        } else if(json["@id"]) {
            return json["@id"];
        }
    }
    return "";
}

function _rotateMargins(margins, rotation) {
  // Normalize the rotation to a value between 0 and 270
  const normalizedRotation = ((rotation % 360) + 360) % 360;

  const { top, right, bottom, left } = margins;

  switch (normalizedRotation) {
    case 0:
      return { top, right, bottom, left };
    case 90:
        return { top: right, right: bottom, bottom: left, left: top };
    case 180:
        return { top: bottom, right: left, bottom: top, left: right };
    case 270:
        return { top: left, right: top, bottom: right, left: bottom };
    default:
      throw new Error("Rotation must be a multiple of 90 degrees (0, 90, 180, 270).");
  }
}


